import { useMemo, useState, type FormEvent } from 'react'
import { useAuth } from '../AuthProvider'
import { useAppPath } from '../nav'
import { Button } from '../../components/ui/Button'

const SUPPORT_EMAIL = 'support@perfectpredict.com'
const SUPPORT_HOURS = 'Monday–Friday · market hours'

type Topic = {
  id: string
  title: string
  detail: string
  subject: string
}

const TOPICS: Topic[] = [
  {
    id: 'live',
    title: 'Live account',
    detail: 'Creation, login, or server access',
    subject: 'Live account help',
  },
  {
    id: 'deposit',
    title: 'Deposits',
    detail: 'Funding status or credit delay',
    subject: 'Deposit help',
  },
  {
    id: 'withdraw',
    title: 'Withdrawals',
    detail: 'Request status or wallet details',
    subject: 'Withdrawal help',
  },
  {
    id: 'mt5',
    title: 'Trade Now / MT5',
    detail: 'In-app Trade Now, prices, or connection help',
    subject: 'Trade access help',
  },
]

export function SupportPage() {
  const { client } = useAuth()
  const appPath = useAppPath()
  const [copied, setCopied] = useState(false)
  const [topicId, setTopicId] = useState(TOPICS[0].id)
  const [subject, setSubject] = useState(TOPICS[0].subject)
  const [message, setMessage] = useState('')
  const [sentHint, setSentHint] = useState('')

  const selectedTopic = useMemo(
    () => TOPICS.find((t) => t.id === topicId) || TOPICS[0],
    [topicId],
  )

  const clientRef = client?.id != null ? String(client.id) : '—'
  const clientEmail = (client?.email || '').trim() || '—'

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  function selectTopic(topic: Topic) {
    setTopicId(topic.id)
    setSubject(topic.subject)
    setSentHint('')
  }

  function buildMailBody() {
    return [
      message.trim() || '(Please describe your issue)',
      '',
      '—',
      `Client reference: ${clientRef}`,
      `Portal email: ${clientEmail}`,
      `Topic: ${selectedTopic.title}`,
      '',
      'Do not include MT5 passwords in this email.',
    ].join('\n')
  }

  function openMailComposer(e: FormEvent) {
    e.preventDefault()
    setSentHint('')
    const sub = (subject.trim() || selectedTopic.subject).slice(0, 120)
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(sub)}&body=${encodeURIComponent(buildMailBody())}`
    window.location.href = href
    setSentHint('Your email app should open with the message ready to send.')
  }

  return (
    <section className="cp-page cp-support">
      <header className="cp-support__head">
        <div>
          <h1>Support</h1>
          <p>Help with Live trading, deposits, withdrawals, and MT5 access.</p>
        </div>
        <p className="cp-support__hours">
          <span>Hours</span>
          <strong>{SUPPORT_HOURS}</strong>
        </p>
      </header>

      <div className="cp-support__layout">
        <section className="cp-support__panel" aria-label="Contact support">
          <div className="cp-support__panel-head">
            <h2>Contact support</h2>
            <p className="cp-muted">Choose a topic, then send a short message.</p>
          </div>

          <div className="cp-support__topics" role="group" aria-label="Help topics">
            {TOPICS.map((topic) => (
              <button
                key={topic.id}
                type="button"
                className={`cp-support__topic${topicId === topic.id ? ' is-active' : ''}`}
                onClick={() => selectTopic(topic)}
              >
                <strong>{topic.title}</strong>
                <span>{topic.detail}</span>
              </button>
            ))}
          </div>

          <form className="cp-support__form" onSubmit={openMailComposer}>
            <label className="cp-field">
              Subject
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={120}
                required
              />
            </label>
            <label className="cp-field">
              Message
              <textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe what you need help with…"
                required
              />
            </label>
            <p className="cp-support__meta">
              Client ref <strong className="cp-num">{clientRef}</strong>
              {clientEmail !== '—' ? (
                <>
                  {' '}
                  · <span>{clientEmail}</span>
                </>
              ) : null}
            </p>
            <div className="cp-support__actions">
              <Button type="submit" variant="primary">
                Send to Support
              </Button>
              <Button type="button" variant="secondary" onClick={() => void copyEmail()}>
                {copied ? 'Email copied' : 'Copy email'}
              </Button>
            </div>
            {sentHint ? (
              <p className="cp-ok cp-support__sent" role="status">
                {sentHint}
              </p>
            ) : null}
          </form>
        </section>

        <aside className="cp-support__side">
          <section className="cp-support__card">
            <h2>Email desk</h2>
            <a className="cp-support__email" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
            <p className="cp-muted">
              Prefer email directly? Use the address above or open your mail app from the form.
            </p>
            <div className="cp-support__actions">
              <a className="pp-btn pp-btn--secondary" href={`mailto:${SUPPORT_EMAIL}`}>
                Open email app
              </a>
            </div>
          </section>

          <section className="cp-support__card">
            <h2>Before you write</h2>
            <ul className="cp-support__checklist">
              <li>Never share MT5 passwords</li>
              <li>Include your client reference</li>
              <li>Use a masked MT5 login (last digits only)</li>
              <li>Add deposit / withdrawal reference if related</li>
            </ul>
          </section>

          <section className="cp-support__card cp-support__card--links">
            <h2>Quick links</h2>
            <div className="cp-support__links">
              <Button to={appPath('/app/accounts')} variant="ghost" size="sm">
                Trading Accounts
              </Button>
              <Button to={appPath('/app/deposits')} variant="ghost" size="sm">
                Deposits
              </Button>
              <Button to={appPath('/app/withdrawals')} variant="ghost" size="sm">
                Withdrawals
              </Button>
            </div>
          </section>
        </aside>
      </div>
    </section>
  )
}
